import {
  TimelinePaymentEvent,
  TimelinePaymentOrder,
  TreatmentPaymentLedger,
  TreatmentPaymentOrder,
} from '@/components/Timeline/types';
import { fetchBot } from '@/services/services';
import { mapPaymentOrderToTimelineItems } from '@/services/Timeline/paymentCards';
import {
  CHARGE_IDENTIFIER_SYSTEM,
  CHECKOUT_SESSION_IDENTIFIER_SYSTEM,
  readPaymentAttribution,
} from '@/services/paymentProvenance';
import { createReference } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const BOT_NAME = 'create-treatment-payment';

const EMPTY_LEDGER: TreatmentPaymentLedger = {
  checkoutSessionId: null,
  entries: [],
  truncated: false,
  partial: false,
};

export interface PaymentOrderState {
  order: TreatmentPaymentOrder | null;
  cards: (TimelinePaymentOrder | TimelinePaymentEvent)[];
  /** The charge-level record of money that actually moved. Exposed because the
   *  Intake and billing panel needs a settlement date and a refund total, and
   *  `sumRefunded` is the only place that arithmetic may live. */
  ledger: TreatmentPaymentLedger;
  loading: boolean;
  error: string | undefined;
  refresh: () => Promise<void>;
}

/**
 * Reads the patient's current treatment payment order through the
 * create-treatment-payment bot, and maps it to timeline cards.
 *
 * **Call this once per patient view and share the result.** The timeline card and
 * the create-order drawer must read the same instance, or creating an order
 * refreshes the drawer while the card on the timeline stays stale.
 *
 * A patient with no order is `order: null` with no error — most patients have
 * never been charged. A bot rejection is always an error, never silently an empty
 * order, so a refusal or a payments-API outage cannot be mistaken for "nothing
 * owed". The cards degrade to absent either way, which is what keeps an outage
 * from blanking the patient view.
 */
export function usePaymentOrder(patient: Patient | undefined): PaymentOrderState {
  const medplum = useMedplum();
  const [order, setOrder] = useState<TreatmentPaymentOrder | null>(null);
  const [ledger, setLedger] = useState<TreatmentPaymentLedger>(EMPTY_LEDGER);
  const [recordedBy, setRecordedBy] = useState<ReadonlyMap<string, string>>(new Map());
  const [createdBy, setCreatedBy] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(!!patient);
  const [error, setError] = useState<string | undefined>();

  // Keyed on the patient id rather than the Patient object: usePatient replaces
  // that object on every `Patient?_id=` subscription push, so depending on its
  // identity would re-read the payment order on every unrelated demographic edit
  // — one bot call and one bariendo-be round trip per edit, per open timeline.
  const patientReference = useMemo(
    () => (patient ? createReference(patient) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above: the reference must track the id, not the object identity
    [patient?.id]
  );

  /**
   * Guards against a late response overwriting a newer one.
   *
   * `PatientTimelineHeader` is rendered without a `key`, so React does not
   * remount this tree when the `:patientId` route param changes (contrast the
   * DoseSpot iframe, which carries an explicit key for exactly this reason).
   * Without this counter, patient A's `orderStatus` resolving after patient B's
   * would write A's balance onto B's timeline — a wrong-patient financial
   * figure, and one step from a wrong-patient payment action.
   *
   * A counter rather than a patient-id comparison so it also covers two
   * in-flight reads for the *same* patient landing out of order.
   */
  const latestRequest = useRef(0);
  // The patient on screen now. A refresh bound to an earlier patient (a write that settles after
  // a switch, BAR-3069) must neither take a request id nor land: it would cancel this patient's
  // read and write the earlier patient's order in its place.
  const currentReference = useRef(patientReference?.reference);
  currentReference.current = patientReference?.reference;

  const refresh = useCallback(async () => {
    if (!patientReference || patientReference.reference !== currentReference.current) {
      return;
    }

    const requestId = ++latestRequest.current;
    const superseded = () => latestRequest.current !== requestId;

    setLoading(true);
    setError(undefined);

    try {
      const result = await fetchBot<{ order: TreatmentPaymentOrder | null }>(medplum, BOT_NAME, {
        operation: 'orderStatus',
        patient: patientReference,
      });

      if (superseded()) {
        return;
      }

      setOrder(result.order);

      // Only worth a second round trip once we know an order exists — the ledger
      // fans out a charge read per payment leg on the backend.
      if (!result.order) {
        setLedger(EMPTY_LEDGER);
        setRecordedBy(new Map());
        setCreatedBy(undefined);
        return;
      }

      const ledgerResult = await fetchBot<{ ledger: TreatmentPaymentLedger }>(medplum, BOT_NAME, {
        operation: 'ledger',
        patient: patientReference,
      });

      if (superseded()) {
        return;
      }

      setLedger(ledgerResult.ledger);

      const [chargeAttribution, orderAttribution] = await Promise.all([
        readRecordedBy(medplum, ledgerResult.ledger),
        readCreatedBy(medplum, result.order.checkoutSessionId),
      ]);

      setRecordedBy(chargeAttribution);
      setCreatedBy(orderAttribution);
    } catch (e) {
      if (superseded()) {
        return;
      }

      setError(e instanceof Error ? e.message : 'Could not load the payment order');
      setOrder(null);
      setLedger(EMPTY_LEDGER);
      setRecordedBy(new Map());
      setCreatedBy(undefined);
    } finally {
      if (!superseded()) {
        setLoading(false);
      }
    }
  }, [medplum, patientReference]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The order now carries its real createdAt, so no client-side stamp is needed.
  const cards = useMemo(
    () => mapPaymentOrderToTimelineItems(order, ledger, new Date(), recordedBy, createdBy),
    [order, ledger, recordedBy, createdBy]
  );

  return { order, cards, ledger, loading, error, refresh };
}

/**
 * Who keyed each hand-recorded charge, from its `Provenance`.
 *
 * Only charges recorded by hand have one — a webhook-written charge is simply
 * absent from the map, and that absence is what the card renders as "system".
 */
async function readRecordedBy(
  medplum: ReturnType<typeof useMedplum>,
  ledger: TreatmentPaymentLedger
): Promise<ReadonlyMap<string, string>> {
  return readPaymentAttribution(
    medplum,
    CHARGE_IDENTIFIER_SYSTEM,
    ledger.entries.map((entry) => entry.chargeId),
    'usePaymentOrder'
  );
}

/**
 * Who created the order — the win attribution (BAR-2614).
 *
 * Absent for any order created before this shipped, and for every order the
 * legacy Close path built. The card must render that absence as unattributed
 * rather than guessing, because the alternative is crediting a sale to the
 * wrong person.
 */
async function readCreatedBy(
  medplum: ReturnType<typeof useMedplum>,
  checkoutSessionId: string
): Promise<string | undefined> {
  const byOrder = await readPaymentAttribution(
    medplum,
    CHECKOUT_SESSION_IDENTIFIER_SYSTEM,
    [checkoutSessionId],
    'usePaymentOrder'
  );

  return byOrder.get(checkoutSessionId);
}
